import classnames from 'classnames';
import { isNumber } from 'lodash';
import React from 'react';
import type { IPosition, ISize } from '@/version-types';
import { useFramework } from '../hooks';

export const BaseOperateView = React.forwardRef<
    HTMLDivElement,
    {
        layer: ISize & IPosition & { rotate?: number };
        children?: React.ReactNode;
        style?: React.CSSProperties;
        topLimitClassName?: string;
        rightLimitClassName?: string;
        offsetTop?: number;
        offsetRight?: number;
        /** 是否是 Edgeless 坐标系 */
        isEdgelessCoord?: boolean;
    }
>(
    (
        {
            layer,
            children,
            offsetTop,
            offsetRight,
            style = {},
            topLimitClassName = '',
            rightLimitClassName = '',
            isEdgelessCoord = false,
        },
        ref,
    ) => {
        const operateRef = React.useRef<HTMLDivElement>(null);
        const limitStateRef = React.useRef({
            isTopLimit: false,
            isRightLimit: false,
            topLimitClassName: '',
            rightLimitClassName: '',
        });
        React.useImperativeHandle(ref, () => operateRef.current as HTMLDivElement);

        const framework = useFramework();
        const { transform, editor } = framework.service;
        const getViewState = React.useCallback(() => {
            const { width, height, left, rotate = 0 } = layer;
            const { zoom: scale, translateX, translateY } = framework.service.edgelessCanvas;
            const offset = isEdgelessCoord ? 0 : transform.calcLayerOffsetTop(layer);
            const top = layer.top + offset;
            const transformTop = top * scale + translateY;
            const transformLeft = left * scale + translateX;

            /** 边缘检测 */
            let isTopLimit = false;
            let isRightLimit = false;
            if (isNumber(offsetTop) || isNumber(offsetRight)) {
                const viewportRect = editor.getBoundingClientRect();
                if (isNumber(offsetTop)) {
                    if (transformTop - offsetTop < 0) {
                        isTopLimit = true;
                    }
                }
                if (isNumber(offsetRight)) {
                    if (transformLeft + width * scale + offsetRight > viewportRect.width) {
                        isRightLimit = true;
                    }
                }
            }

            return {
                width: width * scale,
                height: height * scale,
                transform: `translate(${transformLeft}px, ${transformTop}px) rotate(${rotate}deg)`,
                isTopLimit,
                isRightLimit,
            };
        }, [editor, framework.service.edgelessCanvas, isEdgelessCoord, layer, offsetRight, offsetTop, transform]);

        const syncLimitClass = React.useCallback(
            (node: HTMLDivElement, isTopLimit: boolean, isRightLimit: boolean) => {
                const prev = limitStateRef.current;
                const isTopLimitChange = prev.isTopLimit !== isTopLimit;
                const isRightLimitChange = prev.isRightLimit !== isRightLimit;

                if (isTopLimitChange) {
                    node.classList.toggle('base-operate-top-limit', isTopLimit);
                }
                if (isRightLimitChange) {
                    node.classList.toggle('base-operate-right-limit', isRightLimit);
                }

                if (prev.topLimitClassName && prev.topLimitClassName !== topLimitClassName) {
                    node.classList.remove(prev.topLimitClassName);
                }
                if (topLimitClassName && (isTopLimitChange || prev.topLimitClassName !== topLimitClassName)) {
                    node.classList.toggle(topLimitClassName, isTopLimit);
                }

                if (prev.rightLimitClassName && prev.rightLimitClassName !== rightLimitClassName) {
                    node.classList.remove(prev.rightLimitClassName);
                }
                if (rightLimitClassName && (isRightLimitChange || prev.rightLimitClassName !== rightLimitClassName)) {
                    node.classList.toggle(rightLimitClassName, isRightLimit);
                }

                limitStateRef.current = {
                    isTopLimit,
                    isRightLimit,
                    topLimitClassName,
                    rightLimitClassName,
                };
            },
            [rightLimitClassName, topLimitClassName],
        );

        const syncDom = React.useCallback(() => {
            const node = operateRef.current;
            if (!node) return;

            const viewState = getViewState();
            const width = `${viewState.width}px`;
            const height = `${viewState.height}px`;
            if (node.style.width !== width) {
                node.style.width = width;
            }
            if (node.style.height !== height) {
                node.style.height = height;
            }
            if (node.style.transform !== viewState.transform) {
                node.style.transform = viewState.transform;
            }
            syncLimitClass(node, viewState.isTopLimit, viewState.isRightLimit);
        }, [getViewState, syncLimitClass]);

        const syncTransform = React.useCallback(() => {
            const node = operateRef.current;
            if (!node) return;

            const viewState = getViewState();
            if (node.style.transform !== viewState.transform) {
                node.style.transform = viewState.transform;
            }
            syncLimitClass(node, viewState.isTopLimit, viewState.isRightLimit);
        }, [getViewState, syncLimitClass]);

        React.useLayoutEffect(() => {
            syncDom();

            const { edgelessCanvas, events } = framework.service;
            const zoomHandle = edgelessCanvas.onZoom(syncDom);
            const panHandle = edgelessCanvas.onPan(syncTransform);

            return () => {
                events.off(zoomHandle);
                events.off(panHandle);
            };
        }, [framework.service, syncDom, syncTransform]);

        const viewState = getViewState();

        return (
            <div
                ref={operateRef}
                className={classnames('base-operate', {
                    [topLimitClassName]: viewState.isTopLimit,
                    [rightLimitClassName]: viewState.isRightLimit,
                    'base-operate-top-limit': viewState.isTopLimit,
                    'base-operate-right-limit': viewState.isRightLimit,
                })}
                style={{
                    width: viewState.width,
                    height: viewState.height,
                    transform: viewState.transform,
                    ...style,
                }}
            >
                {children}
            </div>
        );
    },
);
